import {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { DeleteIcon, EditBoxIcon, PCDAlignmentIcon } from '@editor/assets/icons'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { PointArray } from '@editor/interfaces/attribute'
import { InspectionArea } from '@editor/interfaces/inspection'
import { deletePCDAlignment } from '@editor/services/PCDAlignment'
import { withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { reloadAreas } from '@editor/store/page'
import { Divider, DividerLine } from '@infraloop/common'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Matrix4, PerspectiveCamera, Vector3 } from 'three'

import { AlignedArea, Alignment, Tools } from '../definitions'
import {
  LOCAL_FLAGS,
  reset,
  setCurrentTool,
  setLeftAnchors,
  setLeftInspectionArea,
  setLeftProgress,
  setRightAnchors,
} from '../store'
import { collectConnectedAreas } from '../utils'

/**
 * FPS to reposition the action container.
 * Value is duration per frame in ms.
 */
const FPSMs = 1000 / 45

export interface PCDActionsForwardRef {
  onLeftAreaSwitch: (area: InspectionArea) => void
}

const PCDActionsFn: ForwardRefRenderFunction<PCDActionsForwardRef, { camera: PerspectiveCamera }> = (
  { camera },
  ref,
) => {
  const navigate = useNavigate()

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const currentHoveringAreaId = useSelector((state: RootState) => state.pcdAlignment.currentHoveringArea)
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const project = useSelector((state: RootState) => state.page.project)
  const canvasRect = useSelector((state: RootState) => state.pcdAlignment.canvasRect)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)

  // States
  const [deletingIds, setDeletingIds] = useState<[string, string][]>([])

  /**
   * Get the list of connected areas.
   */
  const connectedAreas = useMemo(() => {
    if (!inspectionArea) return []

    return collectConnectedAreas(
      {
        current: {
          area: inspectionArea,
          anchors: [],
          center: new Vector3(0, 0, 0),
        },
        matrix: new Matrix4().identity(),
        inverted: false,
      },
      inspectionArea,
      inspectionAreas,
    )
  }, [inspectionArea, inspectionAreas])

  const connectedHoveredAreas = useMemo(
    () =>
      connectedAreas.filter(
        (aligment) =>
          (aligment.current.area.inspection_area_id === currentHoveringAreaId && aligment.other) ||
          aligment.other?.area.inspection_area_id === currentHoveringAreaId,
      ),
    [currentHoveringAreaId, connectedAreas],
  )

  const requestRef = useRef<number>(undefined)
  const previousTimeRef = useRef<number>(undefined)

  /**
   * Reposition the action container.
   *
   * @param time Current time in ms.
   * @param rect Canvas rectangle.
   * @param hoveredAreaId ID of the hovered area.
   * @param connections List of connected areas.
   */
  const animate = (
    time: DOMHighResTimeStamp,
    rect: { x: number; y: number; width: number; height: number },
    hoveredAreaId: string,
    connections: AlignedArea[],
  ) => {
    if (previousTimeRef.current != null) {
      const deltaTime = time - previousTimeRef.current
      if (deltaTime > FPSMs) {
        connections.forEach((alignment) => {
          const center = alignment.current.anchors
            .reduce((acc, pos) => acc.add(pos), new Vector3(0, 0, 0))
            .divideScalar(3)
          const projected = center.project(camera)

          // 2. Convert from NDC to screen coordinates
          const screenX = Math.round(((projected.x + 1) / 2) * rect.width)
          const screenY = Math.round(((-projected.y + 1) / 2) * rect.height)

          // 3. Update the position of the container
          const container = document.getElementById(
            `alignment-actions-container-${alignment.current.area.inspection_area_id}`,
          )
          if (container) {
            container.classList.remove('hidden')
            container.style.transform = `translate(-50%, -50%) translate(${screenX}px, ${screenY}px)`
          }
        })

        previousTimeRef.current = time
      }
    } else {
      previousTimeRef.current = time
    }

    requestRef.current = requestAnimationFrame((t) => animate(t, rect, hoveredAreaId, connections))
  }

  /**
   * Hide the action container when the user hovers out of an area.
   */
  useEffect(() => {
    if (currentTool !== Tools.Overview) return

    const hoveredAreaIDs = connectedHoveredAreas.map((alignment) => alignment.current.area.inspection_area_id)
    const nonHoveredAreas = connectedAreas.filter(
      (alignment) => !hoveredAreaIDs.includes(alignment.current.area.inspection_area_id),
    )

    nonHoveredAreas.forEach((alignment) => {
      const container = document.getElementById(
        `alignment-actions-container-${alignment.current.area.inspection_area_id}`,
      )
      if (container) {
        container.classList.add('hidden')
      }
    })
  }, [connectedHoveredAreas, connectedAreas, currentTool])

  /**
   * Clears all action containers when user leaves Overview.
   */
  useEffect(() => {
    if (currentTool === Tools.Overview) return

    const containers: NodeListOf<HTMLElement> = document.querySelectorAll('.alignment-actions-container')
    containers.forEach((container) => {
      container.classList.add('hidden')
    })
  }, [currentTool])

  /**
   * Reposition the action container when the user hovers over an area.
   */
  useEffect(
    () => {
      if (!currentHoveringAreaId || currentTool !== Tools.Overview) {
        return () => null
      }

      requestRef.current = requestAnimationFrame((t) =>
        animate(t, canvasRect, currentHoveringAreaId, connectedHoveredAreas),
      )
      return () => cancelAnimationFrame(requestRef.current || 0)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentHoveringAreaId, canvasRect, currentTool, connectedHoveredAreas],
  )

  /**
   * Run deletion process for a connection.
   */
  const handleDelete = useCallback(
    (areaIds: [string, string]) => {
      if (!project) return

      setDeletingIds((prev) => [...prev, areaIds])

      void withPromiseContained(async () => {
        const token = await getAccessToken()
        if (!token) return

        const result = await deletePCDAlignment(token, project.project_id, areaIds, showErrorModal)
        if (!result) return

        dispatch(reloadAreas())
        dispatch(reset())
      }).finally(() => {
        setDeletingIds((prev) => prev.filter((id) => !isEqual(id.sort(), areaIds.sort())))
      })
    },
    [project, getAccessToken, showErrorModal, dispatch],
  )

  /**
   * Handle edit action.
   * Will set both left and right area, plus switch to the appropriate tool.
   *
   * @param alignedArea The aligned area data.
   * @param baseArea The area to be used as base. If not provided, it will be determined from the aligned area data.
   */
  const handleEdit = useCallback(
    (alignedArea: AlignedArea, baseArea?: InspectionArea) => {
      if (!project) return

      let base: Alignment | undefined
      let target: Alignment | undefined
      let withBaseFlipped = false
      if (baseArea) {
        withBaseFlipped = alignedArea.inverted
          ? alignedArea.current.area.inspection_area_id === baseArea.inspection_area_id
          : alignedArea.current.area.inspection_area_id !== baseArea.inspection_area_id
        base =
          alignedArea.current.area.inspection_area_id === baseArea.inspection_area_id
            ? alignedArea.current
            : alignedArea.other
        target =
          alignedArea.current.area.inspection_area_id === baseArea.inspection_area_id
            ? alignedArea.other
            : alignedArea.current
      } else {
        base = alignedArea.inverted ? alignedArea.other : alignedArea.current
        target = alignedArea.inverted ? alignedArea.current : alignedArea.other
      }

      if (!base || !target) return

      // Get the raw alignment data. We need the untransformed data to edit the alignment.
      const alignment = base.area.pcd_alignments ? base.area.pcd_alignments[target.area.inspection_area_id] : null
      if (!alignment) return

      void navigate(`/projects/${project.project_id}/pcd-alignment?area=${base.area.inspection_area_id}`, {
        state: {
          navigated: true,
        },
      })
      dispatch(setCurrentTool(Tools.PCDAlignment))
      dispatch(setLeftInspectionArea(target.area))
      dispatch(
        setLeftAnchors(
          (withBaseFlipped ? alignment.target_anchor_positions : alignment.base_anchor_positions) as PointArray[],
        ),
      )
      dispatch(
        setRightAnchors(
          (withBaseFlipped ? alignment.base_anchor_positions : alignment.target_anchor_positions) as PointArray[],
        ),
      )
    },
    [project, navigate, dispatch],
  )

  useImperativeHandle(ref, () => ({
    onLeftAreaSwitch: (leftArea: InspectionArea) => {
      if (!inspectionArea) return

      const alignment = connectedAreas.find(
        (al) =>
          (al.current.area.inspection_area_id === leftArea.inspection_area_id &&
            al.other?.area.inspection_area_id === inspectionArea?.inspection_area_id) ||
          (al.other?.area.inspection_area_id === leftArea.inspection_area_id &&
            al.current.area.inspection_area_id === inspectionArea?.inspection_area_id),
      )

      LOCAL_FLAGS.ignoreLeftCheck = true
      if (alignment) {
        handleEdit(alignment, inspectionArea)
      } else {
        dispatch(setLeftProgress(0))
        dispatch(setLeftInspectionArea(leftArea))
        dispatch(setLeftAnchors([]))
        dispatch(setRightAnchors([]))
      }
    },
  }))

  return (
    <Box w="100%" h="100%" position="absolute" top="0" left="0" pointerEvents="none" overflow="hidden">
      {connectedAreas.map((alignment) => (
        <VStack
          key={alignment.current.area.inspection_area_id}
          className="alignment-actions-container hidden"
          id={`alignment-actions-container-${alignment.current.area.inspection_area_id}`}
        >
          <VStack className="pcd-names">
            <Text>{alignment.current.area.inspection_area_name}</Text>
            <HStack w="100%" h="16px">
              <Icon as={PCDAlignmentIcon} boxSize="1.3rem" color="gray2.500" />
              <Divider>
                <DividerLine borderStyle="dashed" borderColor="gray2.500" />
              </Divider>
            </HStack>
            <Text>{alignment.other?.area.inspection_area_name}</Text>
          </VStack>
          <Button
            colorPalette="blue"
            w="100%"
            onClick={() => {
              handleEdit(alignment)
            }}
          >
            位置合わせを編集
            <EditBoxIcon size="1.2em" />
          </Button>
          <Button
            w="100%"
            bg="gray2.600"
            color="white"
            _hover={{
              bg: 'gray2.700',
            }}
            onClick={() => {
              if (alignment.other)
                handleDelete([alignment.current.area.inspection_area_id, alignment.other.area.inspection_area_id])
            }}
            loading={deletingIds.some((id) =>
              isEqual(
                id.sort(),
                [alignment.current.area.inspection_area_id, alignment.other?.area.inspection_area_id].sort(),
              ),
            )}
            loadingText="削除中..."
          >
            位置合わせを削除
            <DeleteIcon size="1.4em" />
          </Button>
        </VStack>
      ))}
    </Box>
  )
}

const PCDActions = forwardRef(PCDActionsFn)
export default PCDActions
